import { Route, Outlet, Navigate, useSearchParams } from 'react-router-dom';
import { type RouteRegistryEntry } from 'FrontRoyalRouteRegistry/FrontRoyalRouteRegistry.types';
import { ProtectedRoute } from 'ProtectedRoute';
import { getCohort, type CurrentUser } from 'Users';
import { useCurrentUser } from 'FrontRoyalAngular';
import { StudentNetworkV2 } from './StudentNetworkV2';
import { MapView } from './views/MapView';
import { PeopleView } from './views/PeopleView';
import { EventsView } from './views/EventsView';
import { MessagesView } from './views/MessagesView';

/** Redirect to map if messaging is not enabled for the current user. */
function MessagesGuard() {
    const user = useCurrentUser();
    if (!user?.messagingEnabled) return <Navigate to="/student-network/map" replace />;
    return <MessagesView />;
}

/**
 * Redirect to the Fix Payment page when the user is behind on payments AND
 * their relevant cohort's graduation date has passed. See Trello WGtEtcBt.
 * Keeps the Network tab visible in the header (AppMenu) so the user discovers
 * the restriction by clicking through, rather than the tab silently disappearing.
 */
export function StudentNetworkBillingGuard() {
    const currentUser = useCurrentUser();
    const relevantCohort = getCohort(currentUser);
    const relevantPI = currentUser?.programInclusions?.find(pi => pi.cohortId === relevantCohort?.id);

    // Cohort.graduationDate is a Unix timestamp in seconds; Date.now() is ms.
    const isPastGraduation =
        relevantCohort?.graduationDate != null && Date.now() > relevantCohort.graduationDate * 1000;

    if (relevantPI?.lockedDueToBillingIssue && isPastGraduation) {
        return <Navigate to="/settings/application_status" replace />;
    }

    return <StudentNetworkV2 />;
}

/** Inspect query params on /student-network and redirect to the appropriate sub-route. */
function StudentNetworkRedirect() {
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();
    const qs = search ? `?${search}` : '';

    const eventId = searchParams.get('event-id');
    if (eventId) {
        return <Navigate to={`events/${eventId}`} replace />;
    }
    if (searchParams.has('show-featured-events') || searchParams.has('show-events')) {
        return <Navigate to="events" replace />;
    }
    if (searchParams.has('ids')) {
        const ids = searchParams.get('ids') ?? '';
        const singleId = ids.split(',').filter(Boolean);
        const hasMyClass = searchParams.has('myClass');
        // Single user ID → go directly to profile detail, preserving myClass if present
        if (singleId.length === 1) {
            return <Navigate to={`people/${singleId[0]}${hasMyClass ? '?myClass' : ''}`} replace />;
        }
        return <Navigate to={`people${qs}`} replace />;
    }
    if (searchParams.has('myClass')) {
        return <Navigate to="people?myClass" replace />;
    }
    return <Navigate to="map" replace />;
}

export const STUDENT_NETWORK_V2_ROUTES = {
    STUDENT_NETWORK: { path: '/student-network' },
    MAP: { path: '/student-network/map' },
    PEOPLE: { path: '/student-network/people' },
    EVENTS: { path: '/student-network/events' },
    MESSAGES: { path: '/student-network/messages' },
};

export function hasStudentNetworkAccess({
    currentUser,
    queryParams,
}: {
    currentUser: CurrentUser;
    queryParams: URLSearchParams;
}) {
    if (!currentUser.hasStudentNetworkAccess) return false;
    // Check event access for legacy query params and new /events/:eventId route
    const hasEventParam =
        queryParams.get('event-id') ||
        queryParams.has('show-events') ||
        queryParams.has('show-featured-events') ||
        window.location.pathname.match(/\/student-network\/events\/.+/);
    if (hasEventParam && !currentUser.hasStudentNetworkEventsAccess) return false;

    return true;
}

export function configureStudentNetworkV2Routes(): RouteRegistryEntry[] {
    return [
        {
            routes: STUDENT_NETWORK_V2_ROUTES,
            config: {},
        },
    ];
}

export function getStudentNetworkV2Routes() {
    return (
        <Route id="student-network-v2-routes" element={<Outlet />}>
            <Route
                path={STUDENT_NETWORK_V2_ROUTES.STUDENT_NETWORK.path}
                element={
                    <ProtectedRoute accessPolicy="custom" accessPolicyFn={hasStudentNetworkAccess}>
                        <StudentNetworkBillingGuard />
                    </ProtectedRoute>
                }
            >
                <Route index element={<StudentNetworkRedirect />} />
                <Route path="map" element={<MapView />} />
                <Route path="people/*" element={<PeopleView />} />
                <Route path="events/*" element={<EventsView />} />
                <Route path="messages" element={<MessagesGuard />} />
            </Route>
        </Route>
    );
}
